/**
 * describePolyOrder — resolve a Polymarket asset_id (tokenId) into a
 * human-readable market description by scanning marketStore's currently-
 * loaded games. Returns null when no match is found — typically because
 * the relevant sport tab hasn't loaded yet, or the market expired before
 * the page mounted. Callers fall back to a truncated tokenId in that case.
 *
 * Why client-side: hydra's open-orders endpoint returns raw CLOB orders
 * with no market metadata attached — just asset_id, side, price, size.
 * The marketStore already has every loaded poly market's tokenId pair
 * (yesTokenId + noTokenId per team's VenueBook), so a sweep avoids a relay
 * round-trip per order.
 *
 * Two variants with one cache behind them:
 * - describePolyOrder (FRESH): sweeps every call. Order paths (edit/replace)
 *   use this — see its docstring for why stale tickSize/negRisk can destroy
 *   a resting order.
 * - describePolyOrderCached: render-path variant; resolved labels serve from
 *   cache, unresolved ids retry on a wall-clock cadence.
 */

import { useMarketStore } from '@/stores/marketStore';
import {
  SPORT_TABS,
  type MultiOutcomeEvent,
  type SeriesOutcome,
  type Sport,
  type VenueBook,
} from '@/views/ValueDashboard/types';

export interface PolyOrderDescription {
  /** "OKC @ SAS" — away vs home short codes. */
  matchup: string;
  /** Short code of the team this order's outcome resolves YES for. */
  betTeam: string;
  /** Whether the asset_id matches the team's YES or NO token. With the
   *  per-team Polymarket book convention (each team's row carries its own
   *  YES outcome + the cross-token NO outcome), `'yes'` means "{betTeam}
   *  wins" and `'no'` means "{betTeam} loses". */
  side: 'yes' | 'no';
  sport: Sport;
  /** YYYY-MM-DD as marketStore reports it on the GameRow. */
  date: string;
  /** Market-specific CLOB tick size. Required to safely replace fine-tick orders. */
  tickSize?: string;
  /** Market-specific negRisk flag. Required to safely replace NEG_RISK orders. */
  negRisk?: boolean;
}

function matchToken(assetId: string, book: VenueBook | null | undefined): 'yes' | 'no' | null {
  if (!book) return null;
  if (book.tokenId === assetId) return 'yes';
  if (book.noTokenId === assetId) return 'no';
  return null;
}

function describeMultiOutcomeBook(
  assetId: string,
  sport: Sport,
  event: MultiOutcomeEvent,
  outcome: SeriesOutcome,
): PolyOrderDescription | null {
  const book = outcome.polymarket;
  const side = matchToken(assetId, book);
  if (!side || !book) return null;
  const matchup =
    event.awayCode && event.homeCode
      ? `${event.awayCode} @ ${event.homeCode}`
      : event.title;
  const betTeam = outcome.role === 'draw' ? 'Draw' : outcome.name || outcome.code;
  return {
    matchup,
    betTeam,
    side,
    sport,
    date: event.date ?? '',
    tickSize: book.tickSize,
    negRisk: book.negRisk,
  };
}

/** How long the CACHED variant waits before re-sweeping for an id that
 *  didn't resolve (wall-clock, NOT store-version-denominated: a quiet feed
 *  stops bumping the version entirely, and an open order's label must never
 *  be hostage to feed activity). 3s keeps late-loading sports snappy while a
 *  dead/foreign assetId can't buy the full 14-sport scan on every render. */
const UNRESOLVED_RETRY_MS = 3_000;

interface DescCacheEntry {
  desc: PolyOrderDescription | null;
  at: number; // performance.now() of the sweep that produced this entry
}
const descCache = new Map<string, DescCacheEntry>();

export function _resetDescribePolyOrderCacheForTests(): void {
  descCache.clear();
}

/**
 * FRESH variant — always sweeps the store, then write-through-caches the
 * result (so the cached variant picks up resolutions immediately).
 *
 * ORDER PATHS MUST USE THIS ONE. tickSize/negRisk are NOT session-immutable
 * (hydra re-runs Gamma discovery every ~120s and can change a market's tick
 * regime), and the Polymarket replace flow is non-atomic cancel-then-place —
 * a stale tickSize/negRisk there gets the resting order cancelled and the
 * re-place rejected. A fresh sweep reads the store's ≤120s-fresh values.
 */
export function describePolyOrder(assetId: string): PolyOrderDescription | null {
  if (!assetId) return null;
  const store = useMarketStore.getState();
  const desc = sweepForAssetId(assetId, store);
  descCache.set(assetId, { desc, at: performance.now() });
  return desc;
}

/**
 * CACHED variant — for the per-tick RENDER path only (row labels, card VMs,
 * sport grouping): a resolved entry serves forever (matchup/team/side/sport
 * are session-immutable; its tickSize/negRisk snapshot is display-grade —
 * anything order-shaped re-derives via the fresh variant at click time), and
 * an unresolved entry retries at most every UNRESOLVED_RETRY_MS. This sweep
 * used to run uncached per Poly order per tick from two call sites and was
 * the single worst frame spike on the positions page (~10ms per sport × 14
 * for an unresolvable id).
 */
export function describePolyOrderCached(assetId: string): PolyOrderDescription | null {
  if (!assetId) return null;
  const cached = descCache.get(assetId);
  if (cached) {
    if (cached.desc !== null) return cached.desc;
    if (performance.now() - cached.at < UNRESOLVED_RETRY_MS) return null;
  }
  return describePolyOrder(assetId);
}

function sweepForAssetId(
  assetId: string,
  store: ReturnType<typeof useMarketStore.getState>,
): PolyOrderDescription | null {
  // Iterate every sport's moneyline games — the only market type whose
  // Polymarket book carries per-team token pairs today. Spread/total and
  // series rows route their token data differently and aren't covered by
  // the trader's current Polymarket order surface.
  for (const tab of SPORT_TABS) {
    const games = store.getGames(tab.id, 'moneyline');
    for (const game of games) {
      const poly = game.polymarket;
      if (!poly) continue;
      for (const teamSide of ['away', 'home'] as const) {
        const book = poly[teamSide];
        const side = matchToken(assetId, book);
        if (!side || !book) continue;
        const betTeam = teamSide === 'away' ? game.awayCode : game.homeCode;
        return {
          matchup: `${game.awayCode} @ ${game.homeCode}`,
          betTeam,
          side,
          sport: tab.id,
          date: game.date,
          tickSize: book.tickSize,
          negRisk: book.negRisk,
        };
      }
    }
    for (const event of store.getMultiOutcomeEvents(tab.id)) {
      for (const outcome of event.outcomes) {
        const desc = describeMultiOutcomeBook(assetId, tab.id, event, outcome);
        if (desc) return desc;
      }
    }
  }
  return null;
}
